import { INITIAL_BASKET_STATE } from '@state/basket';
import { INITIAL_FLIGHTS_STATE } from '@state/search-results/flights';
import { INITIAL_EARLY_FLIGHTS_STATE } from '@state/search-results/early-flights';
import { INITIAL_FLIGHTS_SORT_STATE } from '@state/slices/flights-sort';
import { INITIAL_RECENTLY_VIEWED_STATE } from '@state/slices/recently-viewed';
import { INITIAL_COMMON_SEARCH_STATE, INITIAL_TOUR_SEARCH_STATE } from '@state/slices/search';
import { NAVIGATION_INITIAL_STATE } from '@state/slices/navigation';
import { CMS_INITIAL_STATE } from '@state/cms';
import { FILTERS_INITIAL_STATE } from '@state/slices/filters';
import { INITIAL_GUESTS_STATE } from '@state/slices/guests/guestSlice';
import { INITIAL_MODAL_STATE } from '@state/slices/modal/modalSlice';
import { INITIAL_TOURS_STATE } from '@state/slices/search-results/tours/toursSlice';
import { INITIAL_EXTRAS_STATE } from '@state/slices/extras';
import { INITIAL_EXTRAS_OPTIONS_STATE } from '@state/slices/extras-options';
import { INITIAL_SEATMAPS_STATE } from '@state/slices/seat-maps';
import { createWrapper } from 'next-redux-wrapper';
import {
  INITIAL_DESTINATION_STATE,
  INITIAL_HISTORY_STATE,
  INITIAL_LOADING_STATE,
  INITIAL_PACKAGE_REFERENCES_STATE,
  INITIAL_PATH_STATE,
  INITIAL_PROMO_MODAL_STATE,
  INITIAL_SHARE_LINK_STATE,
  INITIAL_UI_STATE
} from '@state/slices/app';
import { INITIAL_PROMO_CODE_STATE } from '@state/slices/promo-code/promoCodeSlice';
import { INITIAL_PAYMENT_FORM_STATE } from '@state/slices/payment/paymentFormSlice';
import { INITIAL_HOTEL_STATE } from '@state/slices/hotel';
import { INITIAL_PRICE_CALENDAR_FILTERS_STATE } from '@state/slices/price-calendar/filter/priceCalendarFilterSlice';
import { INITIAL_PRICE_CALENDAR_MONTHLY_STATE } from '@state/slices/price-calendar/monthly/priceCalendarMonthlySlice';
import { INITIAL_FROM_PRICE_PACKAGES_STATE } from '@state/search-results/from-price-packages/fromPricePackagesOperations';
import { MMB_INITIAL_STATE } from '@state/slices/mmb';
import { INITIAL_AGENT_STATE } from '@state/slices/agent/agentSlice';
import { INITIAL_DEAL_FINDER_STATE } from '@state/slices/deal-finder/dealFinderSlice';
import { INITIAL_ALGOLIA_STATE } from '@state/slices/algolia';
import { INITIAL_DEAL_FINDER_RESULTS_STATE } from '@state/slices/deal-finder-results/dealFinderResultsSlice';
import { INITIAL_DEAL_FINDER_LAST_SEARCHED_STATE } from '@state/slices/deal-finder-last-searched/dealFinderLastSearchedSlice';
import { INITIAL_MEDIA_POST_STATE } from '@state/slices/agent';
import { INITIAL_PROMO_BANNER_STATE } from '@state/slices/promo-banner/promoBannerSlice';
import {
  INITIAL_TOUR_AVAILABILITY_STATE,
  INITIAL_TOUR_FILTERS_STATE,
  INITIAL_TOUR_REFERENCES_STATE,
  INITIAL_TOUR_ROOMS_STATE,
  INITIAL_TOUR_SUMMARY_STATE
} from '@state/slices/tours';
import { INITIAL_DESTINATION_FAQ_STATE } from '@state/slices/destination-faq';
import { INITIAL_BOOKING_DOCUMENTS_STATE } from '@state/slices/mmb/documents';
import { INITIAL_PAYMENT_ERROR_STATE } from '@state/slices/app/payment-error';
import { INITIAL_FOOTER_STATE } from '@state/slices/content';
import { TypedUseSelectorHook, useDispatch as useAppDispatch, useSelector as useAppSelector } from 'react-redux';
import createSagaMiddleware from '@redux-saga/core';
import rootSaga from '@state/store/combineSagas';
import { configureStore } from '@reduxjs/toolkit';
import { persistStore } from 'redux-persist';
import { persistedRootReducer, rootReducer } from '@state/store/combineReducers';
import { isServer } from '@util/is-server';
import { listenerMiddleware } from '@state/store/createListener';
import { initializeListeners } from '@state/store/combineListeners';
import { INITIAL_TOUR_DOSSIERS_STATE } from '@state/slices/tours/tour-dossiers';

export const INITIAL_GLOBAL_APP_STATE: GlobalAppState = {
  navigation: NAVIGATION_INITIAL_STATE,
  basket: INITIAL_BASKET_STATE,
  guests: INITIAL_GUESTS_STATE,
  extras: INITIAL_EXTRAS_STATE,
  extrasOptions: INITIAL_EXTRAS_OPTIONS_STATE,
  seatMaps: INITIAL_SEATMAPS_STATE,
  cms: CMS_INITIAL_STATE,
  filters: FILTERS_INITIAL_STATE,
  searchResults: {
    flights: INITIAL_FLIGHTS_STATE,
    earlyFlights: INITIAL_EARLY_FLIGHTS_STATE,
    tours: INITIAL_TOURS_STATE,
    fromPrices: INITIAL_FROM_PRICE_PACKAGES_STATE
  },
  search: {
    common: INITIAL_COMMON_SEARCH_STATE,
    tours: INITIAL_TOUR_SEARCH_STATE
  },
  flightsSort: INITIAL_FLIGHTS_SORT_STATE,
  footer: INITIAL_FOOTER_STATE,
  app: {
    path: INITIAL_PATH_STATE,
    history: INITIAL_HISTORY_STATE,
    destination: INITIAL_DESTINATION_STATE,
    modal: INITIAL_MODAL_STATE,
    loading: INITIAL_LOADING_STATE,
    packageReferences: INITIAL_PACKAGE_REFERENCES_STATE,
    promoModal: INITIAL_PROMO_MODAL_STATE,
    shareLink: INITIAL_SHARE_LINK_STATE,
    paymentError: INITIAL_PAYMENT_ERROR_STATE,
    uiState: INITIAL_UI_STATE
  },
  recentlyViewed: INITIAL_RECENTLY_VIEWED_STATE,
  promoCode: INITIAL_PROMO_CODE_STATE,
  paymentForm: INITIAL_PAYMENT_FORM_STATE,
  hotel: INITIAL_HOTEL_STATE,
  agent: INITIAL_AGENT_STATE,
  dealFinder: INITIAL_DEAL_FINDER_STATE,
  dealFinderResults: INITIAL_DEAL_FINDER_RESULTS_STATE,
  dealFinderLastSearched: INITIAL_DEAL_FINDER_LAST_SEARCHED_STATE,
  priceCalendar: {
    filters: INITIAL_PRICE_CALENDAR_FILTERS_STATE,
    monthly: INITIAL_PRICE_CALENDAR_MONTHLY_STATE
  },
  mmb: MMB_INITIAL_STATE,
  algolia: INITIAL_ALGOLIA_STATE,
  mediaPosts: {
    posts: INITIAL_MEDIA_POST_STATE.posts,
    tripTypes: INITIAL_MEDIA_POST_STATE.tripTypes
  },
  promoBanner: INITIAL_PROMO_BANNER_STATE.data,
  tours: {
    tourReferences: INITIAL_TOUR_REFERENCES_STATE,
    tourAvailability: INITIAL_TOUR_AVAILABILITY_STATE,
    tourFilters: INITIAL_TOUR_FILTERS_STATE,
    tourDossiers: INITIAL_TOUR_DOSSIERS_STATE,
    tourRooms: INITIAL_TOUR_ROOMS_STATE,
    tourSummary: INITIAL_TOUR_SUMMARY_STATE
  },
  destinationFaq: INITIAL_DESTINATION_FAQ_STATE,
  mmbDocuments: INITIAL_BOOKING_DOCUMENTS_STATE
};

const sagaMiddleware = createSagaMiddleware();

export const storeConfig = (reducer: any, initialState?: GlobalAppState) => {
  const store = configureStore({
    reducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false,
        immutableCheck: false
      })
        .prepend(listenerMiddleware.middleware)
        .concat(sagaMiddleware),
    devTools: process.env.NODE_ENV !== 'production',
    preloadedState: initialState
  });

  initializeListeners();

  (store as any).sagaTask = sagaMiddleware.run(rootSaga);

  if (!isServer) {
    (store as any).__persistor = persistStore(store);
  }

  return store;
};

export const createReduxStore = (
  reducer: any = rootReducer,
  initialState: GlobalAppState = INITIAL_GLOBAL_APP_STATE
) => {
  return storeConfig(reducer, initialState);
};

let store: ReturnType<typeof storeConfig> | undefined;

export const makeStore: any = () => {
  if (isServer) {
    return storeConfig(rootReducer, undefined);
  } else {
    if (!store) {
      store = storeConfig(persistedRootReducer, undefined);
    }
    return store;
  }
};

export type RootState = ReturnType<ReturnType<typeof makeStore>['getState']>;
export type AppDispatch = ReturnType<typeof makeStore>['dispatch'];

export type GlobalAppState = RootState;

const reduxStore = makeStore();
const useDispatch = () => useAppDispatch<AppDispatch>();
const useSelector: TypedUseSelectorHook<RootState> = useAppSelector;

export const { dispatch } = reduxStore;
export { reduxStore, useSelector, useDispatch };
export const storeWrapper = createWrapper<ReturnType<typeof makeStore>>(makeStore);
